import { GENERATOR_MODEL_SLUGS } from '@/consts/generatorModelSlugs';
import type { ModelCatalogCard, ToolkitCatalogSource } from '@/types/toolkit.types';

/**
 * Every model the Toolkit has words for: the design's list, image models first, then video, and after it the
 * models the generator added later. The model row fills each column top to bottom in this order. A card is
 * only shown while the generator offers its model to the person (`resolveToolkitCatalog`), so a model the
 * generator adds later needs its words here too.
 */
const MODEL_CARDS: ModelCatalogCard[] = [
  {
    id: GENERATOR_MODEL_SLUGS.FLUX_3_IMAGE,
    name: 'Flux 3',
    description: 'Black Forest Labs’ model generates detailed, realistic images from text',
    maker: 'flux',
  },
  {
    id: GENERATOR_MODEL_SLUGS.GPT_IMAGE_2_5_FLARE,
    name: 'GPT Image 2.5 - Flare',
    description: 'OpenAI’s model creates images from text and edits existing images',
    maker: 'openai',
  },
  {
    id: GENERATOR_MODEL_SLUGS.GPT_IMAGE_2_5_SUNBURST,
    name: 'GPT Image 2.5 - Sunburst',
    description: 'OpenAI’s model creates images from text and edits existing images',
    maker: 'openai',
  },
  {
    id: GENERATOR_MODEL_SLUGS.NANO_BANANA_2_1,
    name: 'Nano Banana 2.1',
    description: 'Google’s newest Nano Banana generates images from text and edits existing images',
    maker: 'google',
  },
  {
    id: GENERATOR_MODEL_SLUGS.NANO_BANANA_PRO,
    name: 'Nano Banana Pro',
    description: 'Google’s advanced model generates images from text and edits existing images',
    maker: 'google',
  },
  {
    id: GENERATOR_MODEL_SLUGS.NANO_BANANA_2,
    name: 'Nano Banana 2',
    description: 'Google’s model supports image generation from text and editing existing images',
    maker: 'google',
  },
  {
    id: GENERATOR_MODEL_SLUGS.SEEDANCE_2_5_REFERENCE,
    name: 'Seedance 2.5 Reference',
    description: 'ByteDance’s model generates videos that follow your reference images',
    maker: 'bytedance',
  },
  {
    id: GENERATOR_MODEL_SLUGS.SEEDANCE_2_5,
    name: 'Seedance 2.5',
    description: 'ByteDance’s model generates videos from text or images, with built-in audio',
    maker: 'bytedance',
  },
  {
    id: GENERATOR_MODEL_SLUGS.SEEDANCE_2_0_REFERENCE,
    name: 'Seedance 2.0 Reference',
    description: 'ByteDance’s earlier model generates videos guided by reference images',
    maker: 'bytedance',
  },
  {
    id: GENERATOR_MODEL_SLUGS.SEEDANCE_2_0,
    name: 'Seedance 2.0',
    description: 'ByteDance’s earlier model generates videos from text or images',
    maker: 'bytedance',
  },
  {
    id: GENERATOR_MODEL_SLUGS.KLING_4_0,
    name: 'Kling 4.0',
    description: 'Kling’s newest model animates images with cinematic visuals and fluid motion',
    maker: 'kling',
  },
  {
    id: GENERATOR_MODEL_SLUGS.KLING_4_0_REFERENCE,
    name: 'Kling 4.0 Reference',
    description: 'Kling’s model generates videos that keep the look of your reference images',
    maker: 'kling',
  },
  {
    id: GENERATOR_MODEL_SLUGS.KLING_3_0_STANDARD,
    name: 'Kling 3.0 Standard',
    description: 'Animates images with cinematic visuals, fluid motion, and built-in audio',
    maker: 'kling',
  },
  {
    id: GENERATOR_MODEL_SLUGS.KLING_3_0_PRO,
    name: 'Kling 3.0 Pro',
    description: 'Kling 3.0 in higher quality, with cinematic visuals and built-in audio',
    maker: 'kling',
  },
  {
    id: GENERATOR_MODEL_SLUGS.KLING_3_0_4K,
    name: 'Kling 3.0 4K',
    description: 'Kling 3.0 in 4K, with cinematic visuals and built-in audio',
    maker: 'kling',
  },
  {
    id: GENERATOR_MODEL_SLUGS.IDEOGRAM_V4_5,
    name: 'Ideogram V4.5',
    description: 'Ideogram’s model generates images from text, with clear and accurate lettering',
    maker: 'ideogram',
  },
];

/**
 * The Toolkit's rows and their order, from product's tools list. Every `toolId` is a tool owned by the Weave
 * system user, copied on the backoffice's System tools page. A tool card shows that tool's name, description
 * and picture, which are edited in the backoffice, so only its id is here. Model cards keep their words here
 * and open the generator, with their `id` as its `?model=` name. Row words are not translated.
 */
export const TOOLKIT_CATALOG: ToolkitCatalogSource = {
  rows: [
    {
      kind: 'tools',
      id: 'edit-and-enhance',
      title: 'Edit & enhance',
      subtitle: 'Refine images and enhance videos',
      cards: [
        { id: 'illustration-from-text', toolId: 'BYvMNtaMwc4IiUPArNP1TM' },
        { id: 'background-remover', toolId: '6i2fSo49rzCHKwixLHTADL' },
        { id: 'image-upscaler', toolId: 'qGCG6aXKHJmfwWXZM0ukEt' },
        { id: 'video-upscaler', toolId: 'LxqN6SpDkGyBT9ZQrOMcdG' },
        { id: 'replace-background', toolId: 'GzlBuqZnxqkGQ5wTqh7U6a' },
        { id: 'change-aspect-ratio', toolId: 'VBaa9m3E7UbIc1tjKeb3qi' },
        { id: 'image-relighting', toolId: 'Ew0jzK1sa2hZQx4kbfy0YX' },
      ],
    },
    {
      kind: 'models',
      id: 'explore-the-models',
      title: 'Explore the models',
      subtitle: 'Find the right model for image generation, editing, and video',
      cards: MODEL_CARDS,
    },
    {
      kind: 'tools',
      id: 'showcase-your-products',
      title: 'Showcase your products',
      subtitle: 'Create product shots, mockups, and packaging concepts',
      cards: [
        { id: 'virtual-try-on', toolId: 'UDOYGqacCVwq1YvSRILom4' },
        { id: 'add-logo-to-product', toolId: 'r9GH0Dfr32z283pvxMO47F' },
        { id: 'product-shoot', toolId: 'bGiV0ySj2EQeckxlfnqBVS' },
        { id: 'generate-mockup', toolId: 'FuznigwtrET2FhAut3BArq' },
        { id: 'product-variations', toolId: 'UNXCZIHdY4rrAuhFyRDy6G' },
        { id: 'multiview-product-sheet', toolId: '5ObsYd9JmEfoAy4eOLVwPd' },
        { id: 'create-packaging-concepts', toolId: 'zQYvVfBB4PK7q6tceusA8m' },
        { id: 'extract-material', toolId: 'NtylnIwspDeRgi4KqEqIPZ' },
        { id: 'album-mockup', toolId: '4AmqEDMd1ybnQcml2FlzB8' },
      ],
    },
    {
      kind: 'tools',
      id: 'use-a-visual-reference',
      title: 'Use a visual reference',
      subtitle: 'Create with colors and styles from your images',
      cards: [
        { id: 'on-brand-icons', toolId: 'yZmfPuSEAmA48zCyEESAMJ' },
        { id: 'transfer-style', toolId: 'jqoeiJFE1Tke3Zo2IMqNDd' },
        { id: 'extract-color-palette', toolId: 'AmHKyckkWza6VccH77xGTk' },
        { id: 'apply-color-palette', toolId: 'WXzoxMwFC940p6LiSLxuvp' },
      ],
    },
    {
      kind: 'tools',
      id: 'restyle-and-convert',
      title: 'Restyle & convert',
      subtitle: 'Explore new styles, textures, stickers, and vector illustrations',
      // "Change image style from list" belongs second here. It is left out until its tool is copied to the
      // system tools, because a card needs a system tool id.
      cards: [
        { id: 'restyle-an-image', toolId: 'plK4B5CGgBfMx7UG7URMSa' },
        { id: 'sticker-pack', toolId: 'CdWKFlZBazLuWsH7f5y9UA' },
        { id: 'image-to-vector', toolId: '9d6NWaJbdsTcQfecshO5YZ' },
        { id: 'texturize', toolId: 'ezeVlLH4WSJ7svvDUSiSKN' },
      ],
    },
    {
      kind: 'tools',
      id: 'people-and-characters',
      title: 'People & characters',
      subtitle: 'Create headshots, swap faces, and turn photos into avatars',
      cards: [
        { id: 'professional-headshot', toolId: '0wTzH1JUF2bAxdSkAMBHtM' },
        { id: 'ai-face-swap', toolId: 'V0getspYyZPBNStjdUnXot' },
        { id: 'image-to-avatar', toolId: 'ON5m7v44UoSwjOkFVIMiVn' },
      ],
    },
  ],
};
